import { EscHotKeyIcon } from '@modules/common/components/HotKeyIcons';
import { useMuiModalContext } from '@modules/common/components/Modal/ModalContext';
import { useNotificationContext } from '@modules/common/components/Notification/NotificationContext';
import { trackDashboardEvent } from '@modules/dashboard/helpers';
import { useUpdateDashboard } from '@modules/dashboard/hooks/useUpdateDashboard';
import { EditableDashboard } from '@modules/dashboard/types';
import CloseIcon from '@mui/icons-material/Close';
import { LoadingButton } from '@mui/lab';
import {
  Button,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  Stack,
  Switch,
  Typography,
} from '@mui/material';
import { Scope } from 'generated/graphql';
import { useState } from 'react';

interface ChangeDashboardDiscoverabilityDialogContentProps {
  dashboard: EditableDashboard;
}

export const ChangeDashboardDiscoverabilityDialogContent = ({
  dashboard,
}: ChangeDashboardDiscoverabilityDialogContentProps) => {
  const { showSuccessNotification } = useNotificationContext();

  const [isDiscoverable, setIsDiscoverable] = useState(
    dashboard.scope === Scope.Public,
  );

  const { updateDashboard, loading } = useUpdateDashboard();

  const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIsDiscoverable(event.target.checked);
  };

  const { closeModal } = useMuiModalContext();

  const onClose = () => closeModal();

  const updateDiscoverability = async () => {
    await updateDashboard({
      updateRequest: {
        id: dashboard.id,
        viewToken: dashboard.viewToken,
        editToken: dashboard.editToken,
        scope: isDiscoverable ? Scope.Public : Scope.Private,
      },
    });

    showSuccessNotification({
      contentComponent: 'Dashboard discoverability updated successfully.',
      props: {
        isClosable: true,
      },
    });
    onClose();
    trackDashboardEvent({
      eventName: 'Dashboard Details Updated',
      dashboardLink: window.location.href,
      updates: {
        discoverability: isDiscoverable ? 'Discoverable' : 'Not Discoverable',
      },
    });
  };

  return (
    <>
      <DialogTitle>
        <Stack direction="row" justifyContent="space-between">
          <Typography variant="h6">
            Change Discoverability of "{dashboard.title}"
          </Typography>
          <IconButton aria-label="close" onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>
      <Divider />
      <DialogContent>
        <Stack direction="row" justifyContent="space-between" spacing={2}>
          <DialogContentText maxWidth="60%">
            <Typography variant="subtitle2">DASHBOARD DISCOVERY</Typography>
            <Typography variant="caption">
              Discoverable analysis is visible to anyone within the
              organisation. Otherwise, analysis can only be accessed by its
              shareable link.
            </Typography>
          </DialogContentText>
          <Stack direction="row" spacing={2}>
            <FormControlLabel
              control={
                <Switch defaultChecked={isDiscoverable} onChange={onChange} />
              }
              label="Discoverable"
            />
          </Stack>
        </Stack>
      </DialogContent>
      <Divider />
      <DialogActions>
        <Stack direction="row" m={1} spacing={2}>
          <Button
            onClick={onClose}
            variant="outlined"
            endIcon={<EscHotKeyIcon />}
          >
            Cancel
          </Button>
          <LoadingButton
            loading={loading}
            onClick={updateDiscoverability}
            variant="contained"
            autoFocus
          >
            Update
          </LoadingButton>
        </Stack>
      </DialogActions>
    </>
  );
};
